import {
  ConstrainMode,
  DetailsRow,
  IColumn,
  IDetailsGroupDividerProps,
  IDetailsGroupRenderProps,
  IDetailsList,
  IDetailsRowProps,
  IDetailsRowStyleProps,
  IDetailsRowStyles,
  IDragDropEvents,
  IGroup,
  IRenderFunction,
  mergeStyleSets,
  ScrollToMode,
} from '@fluentui/react';
import { IObjectWithKey, IStyleFunctionOrObject } from '@fluentui/utilities';
import AppContext from 'App/AppContext';
import { IPScrollablePane, IPShimmeredDetailsList, IPText, IPTooltipHost } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import { onRenderDetailsHeaderGlobal, onRenderDetailsHeaderNoPaddingTopGlobal } from 'globalFunctions';
import { globalStackItemStylesScroll } from 'globalStyles';
import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';

export interface ItemTypeBase {
  id: string;
}

// Imperative API exposed to parents
export interface IBaseTableList<ItemType> {
  // replaceSelection makes the row the whole selection instead of adding it to what is selected,
  // for a caller that mirrors one row it holds itself. Selecting several rows in a loop leaves it off.
  scroll: (index: number, select?: boolean, items?: ItemType[], replaceSelection?: boolean) => void;
  sort: (key: string) => void;
  // Re-measures the list, for a caller in an IPScrollablePane that observes the pane's width (usePaneResize):
  // the justified columns follow the new width.
  forceUpdate: () => void;
}

export const baseTableListRowHoverClasses = mergeStyleSets({
  row: {
    selectors: {
      ':hover .showOnRowHover': { opacity: 1 },
      '&.is-selected .showOnRowHover': { opacity: 1 },
    },
  },
  action: {
    opacity: 0,
    transition: 'opacity 120ms ease',
    selectors: {
      ':focus-within': { opacity: 1 }, // only when the button (inside) has focus
    },
  },
  groupHeader: {
    selectors: {
      ':hover .showOnRowHover': { opacity: 1 },
      ':focus-within .showOnRowHover': { opacity: 1 },
    },
  },
  // the native drop target around a group header while a row is dragged over it; the marker class is
  // the one Fluent puts on a row being dropped on, so both targets can share one look
  groupDropTarget: {
    selectors: {
      '&.is-dropping': { outline: '1px dashed currentColor', outlineOffset: -1 },
    },
  },
});

interface IDroppableGroupHeaderProps {
  group: IGroup;
  onDrop: (group: IGroup) => void;
  children: React.ReactNode;
}

// The native drop target around one group header. Enter and leave are counted instead of toggled:
// every child of the header fires a leave for the header when the pointer enters the child, which
// would blink the marker off while the pointer is still on the header.
const DroppableGroupHeader = (props: IDroppableGroupHeaderProps) => {
  const [isDropping, setIsDropping] = useState<boolean>(false);
  const depthRef = useRef<number>(0);

  return (
    <div
      className={`${baseTableListRowHoverClasses.groupDropTarget}${isDropping ? ' is-dropping' : ''}`}
      onDragEnter={(ev) => {
        ev.preventDefault();
        depthRef.current += 1;
        setIsDropping(true);
      }}
      onDragOver={(ev) => {
        // without this the browser refuses the drop
        ev.preventDefault();
        ev.dataTransfer.dropEffect = 'move';
      }}
      onDragLeave={() => {
        depthRef.current = Math.max(depthRef.current - 1, 0);
        if (depthRef.current === 0) setIsDropping(false);
      }}
      onDrop={(ev) => {
        ev.preventDefault();
        depthRef.current = 0;
        setIsDropping(false);
        props.onDrop(props.group);
      }}
    >
      {props.children}
    </div>
  );
};

interface IBaseTableListListProps<ItemType = ItemTypeBase> {
  items: ItemType[];
  columns: IColumn[];
  groups?: IGroup[];
  groupProps?: IDetailsGroupRenderProps;
  selectionMode?: IPSelectionMode;
  selectionResetKey?: string;
  layoutMode?: IPDetailsListLayoutMode;
  cacheKeySort?: LocalStorageKeys;
  isLoading?: boolean;
  compact?: boolean;
  disableSort?: boolean;
  disableCannotSelectTooltip?: boolean;
  disableEmptyState?: boolean;
  autoHeight?: boolean;
  hideHeader?: boolean;
  enableRowDragDrop?: boolean;
  noTopPadding?: boolean;
  shimmerLines?: number;
  /** Rendered right under the column headers, above the first row: an add row that lines up with the columns. */
  afterHeader?: JSX.Element | null;
  setColumns: (newColumns: IColumn[]) => void;
  getKey?: (item: ItemType) => string;
  isSortEnabled?: (column: IColumn) => boolean;
  compare?: (a: ItemType, b: ItemType, columnKey: string) => number;
  onSelect?: (items: ItemType[]) => void;
  onRecalcGroupsAfterSort?: (items: ItemType[], column: IColumn) => void;
  onRowDbClick?: (item: ItemType, index?: number | undefined) => void;
  onRenderCol: (item: ItemType, column: IColumn, index?: number | undefined) => JSX.Element | null;
  onRowClick?: (item: ItemType, index?: number | undefined) => void;
  onCanSelect?: (item: ItemType, index?: number | undefined) => boolean;
  onDrop?: (draggedItem: ItemType, targetItem: ItemType, draggedIndex: number, targetIndex: number) => void;
  // A dragged row dropped on a group header. Fluent's GroupedListV2 registers no drop target for its
  // headers, so without this a group that has no rows yet can never receive one.
  onDropOnGroup?: (draggedItem: ItemType, group: IGroup, draggedIndex: number) => void;
}

function BaseTableListInner<ItemType extends unknown>(
  props: IBaseTableListListProps<ItemType>,
  ref: ForwardedRef<IBaseTableList<ItemType>>,
) {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation']);
  const root = useRef<IDetailsList>(null);
  const [showItems, setShowItems] = useState<ItemType[]>([]);
  const [sortLoaded, setSortLoaded] = useState<boolean>(false);
  const [selectionResetKey, setSelectionResetKey] = useState<string>('set');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const lastDragTargetRef = useRef<{ item: ItemType; index: number } | undefined>(undefined);
  // the row being dragged, for the group header drop target; a ref because the header render
  // closures are not re-created on every render (see groupProps below)
  const dragSourceRef = useRef<{ item: ItemType; index: number } | undefined>(undefined);
  const onDropOnGroupRef = useRef(props.onDropOnGroup);
  useEffect(() => {
    onDropOnGroupRef.current = props.onDropOnGroup;
  }, [props.onDropOnGroup]);

  // Keep a ref to always have the latest items for imperative calls
  const itemsRef = useRef<ItemType[]>([]);
  useEffect(() => {
    itemsRef.current = showItems;
  }, [showItems]);

  //
  // Handlers react to changed input of items
  //
  useEffect(() => {
    let newItems: ItemType[] = [];

    if (props.items?.length > 0 && !props.disableSort) {
      newItems = applySortFromLocalStorage(props.items);
    } else {
      newItems = props.items;
    }

    setShowItems(newItems);

    selection.setItems(newItems as ItemTypeBase[], false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.items]);

  // A new reset key means the caller dropped its selection, so the rows have to follow. The list
  // itself only clears on a new setKey when the items array changed in that very same render, and a
  // reset almost always lands one render later than the new items: the caller replaces its data and
  // bumps the key from an effect on it. Without clearing here the rows stay checked while the caller
  // already forgot them, leaving its commands disabled on a visibly selected row.
  useEffect(() => {
    if (props.selectionResetKey) {
      setSelectionResetKey(props.selectionResetKey);
      // guarded: Fluent fires a selection change on every setAllSelected call, also when nothing
      // was selected, and callers must not see an empty selection they never made
      if (selection.getSelectedCount() > 0) selection.setAllSelected(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectionResetKey]);

  //
  // Selection
  //
  const selectionModeRef = useRef(props.selectionMode ?? IPSelectionMode.none);
  const onCanSelectRef = useRef(props.onCanSelect);
  const updateSelectionRef = useRef(props.onSelect);
  const getKeyRef = useRef(props.getKey);

  useEffect(() => {
    selectionModeRef.current = props.selectionMode ?? IPSelectionMode.none;
  }, [props.selectionMode]);
  useEffect(() => {
    onCanSelectRef.current = props.onCanSelect;
  }, [props.onCanSelect]);
  useEffect(() => {
    updateSelectionRef.current = props.onSelect;
  }, [props.onSelect]);
  useEffect(() => {
    getKeyRef.current = props.getKey;
  }, [props.getKey]);

  const selectionRef = useRef<IPSelection<ItemTypeBase>>();
  if (!selectionRef.current) {
    selectionRef.current = new IPSelection<ItemTypeBase>({
      selectionMode: selectionModeRef.current,
      getKey: (item) => {
        if (item.id) return item.id;
        if (!getKeyRef.current) return '';

        return getKeyRef.current?.(item as ItemType);
      },
      canSelectItem: (item, index) => {
        if (selectionModeRef.current === IPSelectionMode.none) return true;
        const fn = onCanSelectRef.current;

        return fn ? fn(item as ItemType, index) : true;
      },
      onSelectionChanged: () => {
        if (selectionModeRef.current === IPSelectionMode.none) return;
        const fn = updateSelectionRef.current;
        if (fn) fn(selectionRef.current?.getSelection() as ItemType[]);
      },
    });
  }

  const selection = selectionRef.current;

  //
  // Sorting
  //
  const compare = (a: ItemType, b: ItemType, key: string, isSortDescending?: boolean): number => {
    if (props.compare) {
      const output = props.compare(a, b, key);

      return isSortDescending ? -output : output;
    } else {
      return 0;
    }
  };

  const applySortFromLocalStorage = (items: ItemType[]): ItemType[] => {
    if (sortLoaded) {
      return applyCurrentSort(items, props.columns);
    } else {
      setSortLoaded(true);
      const newColumns = props.columns;
      if (props.cacheKeySort) {
        const storedSort = getLocalStorageData(appContext, props.cacheKeySort);
        if (storedSort) {
          try {
            const values = storedSort.split(',');
            const colKey = values[0];
            const descending = values[1] === '1';
            const currColumn: IColumn | undefined = newColumns.find((currCol) => colKey === currCol.key);
            if (currColumn) {
              return applySort(items, newColumns, currColumn, descending);
            }
          } catch {
            // ignore
          }
        }
      }

      return applyCurrentSort(items, newColumns);
    }
  };

  const applySort = (
    items: ItemType[],
    columns: IColumn[],
    column?: IColumn,
    isForceDescendingEnabled?: boolean,
  ): ItemType[] => {
    if (!column) return items;
    if (props.isSortEnabled && !props.isSortEnabled(column)) return items;

    const newColumns = [...columns];
    const currColumn: IColumn = newColumns.filter((c) => column.key === c.key)[0];

    if (isForceDescendingEnabled !== undefined) {
      newColumns.forEach((newCol: IColumn) => {
        if (newCol === currColumn) {
          currColumn.isSortedDescending = isForceDescendingEnabled;
          currColumn.isSorted = true;
        } else {
          newCol.isSorted = false;
          newCol.isSortedDescending = true;
        }
      });
    } else {
      newColumns.forEach((newCol: IColumn) => {
        if (newCol === currColumn) {
          currColumn.isSortedDescending = !currColumn.isSortedDescending;
          currColumn.isSorted = true;
        } else {
          newCol.isSorted = false;
          newCol.isSortedDescending = true;
        }
      });
    }

    const newSortedItems = items.sort((a, b) => compare(a, b, currColumn.key, currColumn.isSortedDescending));
    saveSortToLocalStorage(column.key, currColumn.isSortedDescending || false);
    props.setColumns(newColumns);

    if (props.onRecalcGroupsAfterSort) {
      props.onRecalcGroupsAfterSort(newSortedItems, currColumn);
    }

    return newSortedItems;
  };

  const applyCurrentSort = (items: ItemType[], newColumns: IColumn[]): ItemType[] => {
    const curCol = newColumns.find((c) => c.isSorted);
    const descending = curCol?.isSortedDescending ?? false;

    return applySort(items, newColumns, curCol, descending);
  };

  const saveSortToLocalStorage = (key: string, descending: boolean) => {
    if (props.cacheKeySort) {
      const values = [key, descending ? '1' : '0'];
      setLocalStorageData(appContext, props.cacheKeySort, values.join(','));
    }
  };

  const onColumnClick = (ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
    if (props.disableSort) return;
    const newItems = applySort(showItems, props.columns, column);
    setShowItems(newItems);
    selection.setItems(newItems as ItemTypeBase[], false);
  };

  // Fluent UI dragDropEvents for row drag-and-drop
  const dragDropEvents: IDragDropEvents | undefined =
    props.enableRowDragDrop && props.onDrop
      ? {
          canDrop: () => true,
          canDrag: () => true,
          onDragStart: (item?: ItemType, itemIndex?: number) => {
            lastDragTargetRef.current = undefined;
            dragSourceRef.current = undefined;
            if (item) {
              const indexInRows = showItems.indexOf(item);
              if (indexInRows !== -1) {
                setDraggedIndex(indexInRows);
                dragSourceRef.current = { item, index: indexInRows };
              }

              return;
            }

            if (typeof itemIndex === 'number' && itemIndex >= 0 && itemIndex < showItems.length) {
              setDraggedIndex(itemIndex);
              dragSourceRef.current = { item: showItems[itemIndex], index: itemIndex };
            }
          },
          onDragEnter: (item?: ItemType) => {
            if (!item) return '';
            const targetIndex = showItems.indexOf(item);
            if (targetIndex >= 0) lastDragTargetRef.current = { item, index: targetIndex };

            return '';
          },
          onDrop: (item?: ItemType) => {
            const itemIndex = item ? showItems.indexOf(item) : -1;
            const fallbackTarget =
              lastDragTargetRef.current ?? (showItems.length > 0 ? { item: showItems[0], index: 0 } : undefined);
            const targetItem = item ?? fallbackTarget?.item;
            const targetIndex = item !== undefined ? itemIndex : fallbackTarget?.index ?? -1;
            if (
              typeof draggedIndex === 'number' &&
              draggedIndex !== targetIndex &&
              targetItem !== undefined &&
              props.onDrop
            ) {
              const draggedItem = showItems[draggedIndex];
              props.onDrop(draggedItem, targetItem, draggedIndex, targetIndex);
            }
            setDraggedIndex(null);
            lastDragTargetRef.current = undefined;
            dragSourceRef.current = undefined;
          },
          onDragEnd: () => {
            setDraggedIndex(null);
            lastDragTargetRef.current = undefined;
            dragSourceRef.current = undefined;
          },
        }
      : undefined;

  // GroupedListV2 registers no drop target for its group headers (only the rows subscribe to Fluent's
  // drag-drop helper), so a group without rows could never receive a dragged row. The header is made a
  // native drop target here. The drag source and the callback come from refs: the virtualized list
  // re-renders a header on a changed groups array, not on a changed render callback, so anything a
  // header closure captures directly can go stale.
  const hasGroupDrop = props.enableRowDragDrop === true && props.onDropOnGroup !== undefined;
  const groupProps = useMemo((): IDetailsGroupRenderProps | undefined => {
    if (!props.groupProps || !hasGroupDrop) return props.groupProps;

    const renderHeader = props.groupProps.onRenderHeader;
    const onDropOnGroup = (group: IGroup) => {
      const source = dragSourceRef.current;
      if (source && onDropOnGroupRef.current) onDropOnGroupRef.current(source.item, group, source.index);
    };

    return {
      ...props.groupProps,
      onRenderHeader: (
        headerProps?: IDetailsGroupDividerProps,
        defaultRender?: IRenderFunction<IDetailsGroupDividerProps>,
      ): JSX.Element | null => {
        const header = renderHeader ? renderHeader(headerProps, defaultRender) : defaultRender?.(headerProps) ?? null;
        if (!headerProps?.group) return header;

        return (
          <DroppableGroupHeader group={headerProps.group} onDrop={onDropOnGroup}>
            {header}
          </DroppableGroupHeader>
        );
      },
    };
  }, [props.groupProps, hasGroupDrop]);

  //
  // Render helpers
  //
  const onRenderRow = useCallback(
    (
      rowProps?: IDetailsRowProps,
      defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null,
    ): JSX.Element | null => {
      if (rowProps) {
        const mergedStyles: IStyleFunctionOrObject<IDetailsRowStyleProps, IDetailsRowStyles> = (styleProps) => {
          const prev = typeof rowProps.styles === 'function' ? rowProps.styles(styleProps) : rowProps.styles || {};

          return {
            ...prev,
            root: [prev?.root, baseTableListRowHoverClasses.row],
          };
        };

        if (!props.disableCannotSelectTooltip && !selection.canSelectItem(rowProps.item, rowProps.itemIndex)) {
          rowProps.disabled = true;

          return (
            <IPTooltipHost
              content={t('translation:General.List.CannotSelectItem')}
              directionalHint={IPDirectionalHint.topLeftEdge}
            >
              <DetailsRow {...rowProps} styles={mergedStyles} />
            </IPTooltipHost>
          );
        } else {
          return (
            <IPStack
              tokens={{ childrenGap: 0 }}
              onDoubleClick={() => {
                if (props.onRowDbClick) props.onRowDbClick(rowProps.item, rowProps.itemIndex);
              }}
            >
              <DetailsRow {...rowProps} styles={mergedStyles} />
            </IPStack>
          );
        }
      }

      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onRenderItemColumn = (item?: ItemType, index?: number | undefined, column?: IColumn | undefined) => {
    if (item && column) {
      return props.onRenderCol(item, column, index);
    }

    return null;
  };

  //
  // Imperative methods
  //
  // Leaves the row as the only selected one. A row already selected next to it stays selected
  // otherwise, and Fluent then reports both, in the order of the list rather than the order they
  // were picked, so a caller reading the last of them shows the wrong row. Clearing and selecting
  // are one change, so the caller never sees the empty selection in between, and a selection that
  // is already this one row is left alone rather than reported back to the caller that asked for it.
  const selectOnlyIndex = (index: number): void => {
    const selectedIndices = selection.getSelectedIndices();
    if (selectedIndices.length === 1 && selectedIndices[0] === index) return;

    selection.setChangeEvents(false);
    if (selection.getSelectedCount() > 0) selection.setAllSelected(false);
    selection.setIndexSelected(index, true, false);
    selection.setChangeEvents(true);
  };
  const scrollImpl = (index: number, select?: boolean, items?: ItemType[], replaceSelection?: boolean): void => {
    if (select) {
      if (items) {
        selection.setItems(items as ItemTypeBase[], false);
      }
      if (replaceSelection) {
        selectOnlyIndex(index);
      } else {
        selection.setIndexSelected(index, true, false);
      }
    }
    //if new items are passed, root.current does not have them yet, so we use timeout to wait for the next frame
    if (items) {
      setTimeout(() => {
        root.current?.scrollToIndex(index, undefined, ScrollToMode.center);
      }, 100);
    } else {
      root.current?.scrollToIndex(index, undefined, ScrollToMode.center);
    }
  };

  const sortImpl = (key: string): void => {
    if (!key) return;
    const col: IColumn | undefined = props.columns.find((c) => key === c.key);
    const curCol = props.columns.find((c) => c.isSorted);
    if (col && col.key !== curCol?.key) {
      const source = itemsRef.current ?? props.items;
      const newItems = applySort([...source], props.columns, col, false);
      setShowItems(newItems);
      selection.setItems(newItems as ItemTypeBase[], false);
    }
  };

  useImperativeHandle(ref, () => ({
    scroll: scrollImpl,
    sort: sortImpl,
    forceUpdate: () => root.current?.forceUpdate(),
  }));

  //
  // Main render
  //
  if (!props.disableEmptyState && !props.isLoading && showItems.length === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 200 } }}>
        <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
      </IPStack>
    );
  }

  const detailsList = (
    <IPShimmeredDetailsList
      componentRef={root}
      enableShimmer={props.isLoading ?? false}
      shimmerLines={props.shimmerLines ?? 6}
      columns={props.columns}
      groups={props.groups}
      groupProps={groupProps}
      selection={selection as IPSelection<IObjectWithKey>}
      items={showItems}
      setKey={selectionResetKey}
      compact={props.compact}
      selectionPreservedOnEmptyClick={true}
      selectionMode={props.selectionMode ?? IPSelectionMode.none}
      isHeaderVisible={!props.hideHeader}
      layoutMode={props.layoutMode ?? IPDetailsListLayoutMode.justified}
      constrainMode={ConstrainMode.unconstrained}
      onRenderDetailsHeader={(headerProps, defaultRender) => {
        const header = (props.noTopPadding ? onRenderDetailsHeaderNoPaddingTopGlobal : onRenderDetailsHeaderGlobal)(
          headerProps,
          defaultRender,
        );
        if (!props.afterHeader) return header;

        return (
          <>
            {header}
            {props.afterHeader}
          </>
        );
      }}
      onRenderRow={onRenderRow}
      onRenderItemColumn={onRenderItemColumn}
      onColumnHeaderClick={onColumnClick}
      onActiveItemChanged={(item, index) => {
        if (props.onRowClick) props.onRowClick(item, index);
      }}
      dragDropEvents={dragDropEvents}
    />
  );

  if (props.autoHeight) return detailsList;

  return (
    <IPStack verticalFill styles={{ root: { minHeight: 200 } }}>
      <IPStack.Item grow styles={globalStackItemStylesScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>{detailsList}</IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
}

const BaseTableList = forwardRef(BaseTableListInner) as <ItemType extends unknown>(
  props: IBaseTableListListProps<ItemType> & { ref?: React.Ref<IBaseTableList<ItemType>> },
) => JSX.Element;

export default BaseTableList;
